import { IDomainValue } from 'models/common';
import {
	IServiceGroup,
	IServiceGroupsForm,
	IServiceTree,
	IUpdateServiceGroupsRequest,
	UNLINKED_KEY,
} from 'models/shop/serviceGroups';
import { IServicesList } from 'models/shop/shopBookingTemplates';
import { requestHelper } from 'redux/helpers';
import { IStore } from 'redux/IStore';

export function addServiceGroup(getState: () => IStore, serviceGroup: IServiceGroup): Promise<IServiceGroup> {
	if (!serviceGroup) {
		return Promise.reject('serviceGroup missing');
	}
	const body = {
		...serviceGroup,
		id: undefined,
	};
	return requestHelper<IServiceGroup>(
		fetch(
			getState().fetchDefault.baseAPI + 'api/Shop/ServiceGroup',
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'POST',
				body: JSON.stringify(body),
			}),
		),
	);
}

export function deleteServiceGroup(getState: () => IStore, serviceGroupId: string): Promise<any> {
	if (!serviceGroupId) {
		return Promise.reject('serviceGroupId missing');
	}
	return requestHelper(
		fetch(
			getState().fetchDefault.baseAPI + 'api/Shop/ServiceGroup/' + serviceGroupId,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'DELETE',
			}),
		),
	);
}

export function updateServiceGroup(
	getState: () => IStore,
	serviceGroupId: string,
	serviceGroup: IServiceGroup,
): Promise<any> {
	if (!serviceGroupId) {
		return Promise.reject('serviceGroupId missing');
	}
	return requestHelper(
		fetch(
			getState().fetchDefault.baseAPI + 'api/Shop/ServiceGroup/' + serviceGroupId,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'PUT',
				body: JSON.stringify(serviceGroup),
			}),
		),
	);
}

/**
 *
 * @param getState
 * @param officeId Office id
 * @param serviceGroupsToAdd Array of service group objects to be added
 * @param serviceGroupsToUpdate Array of service group objects to be updated
 * @param serviceGroupsToDelete Array of service group ID's to be deleted
 */
export function saveOfficeServiceGroups(
	getState: () => IStore,
	officeId: string,
	serviceGroupsToAdd: IServiceGroup[],
	serviceGroupsToUpdate: IServiceGroup[],
	serviceGroupsToDelete: string[],
): Promise<any> {
	if (!officeId || (!serviceGroupsToAdd?.length && !serviceGroupsToUpdate?.length && !serviceGroupsToDelete?.length)) {
		return Promise.reject('Nothing to update!');
	}

	const serviceGroupRequest: IUpdateServiceGroupsRequest = {
		itemsToAdd: serviceGroupsToAdd,
		itemsToUpdate: serviceGroupsToUpdate,
		itemsToRemove: serviceGroupsToDelete,
	};

	return requestHelper(
		fetch(
			getState().fetchDefault.baseAPI + `api/Shop/${officeId}/SaveOfficeServiceGroups/`,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'POST',
				body: JSON.stringify(serviceGroupRequest),
			}),
		),
	);
}

export function getServiceGroupsByOffice(
	getState: () => IStore,
	officeId: string,
	abortController?: AbortController,
): Promise<IServiceGroup[]> {
	return requestHelper<IServiceGroup[] | IServiceGroup>(
		fetch(getState().fetchDefault.baseAPI + 'api/Shop/' + officeId + '/OfficeServiceGroups', {
			...getState().fetchDefault.requestInit,
			signal: abortController ? abortController.signal : undefined,
		}),
	).then((res) => (Array.isArray(res) ? res : [res]));
}

export function getServiceGroupsByShop(
	getState: () => IStore,
	shopId: string,
	abortController?: AbortController,
): Promise<IServiceGroup[]> {
	return requestHelper<IServiceGroup[] | IServiceGroup>(
		fetch(getState().fetchDefault.baseAPI + 'api/Shop/' + shopId + '/ServiceGroups', {
			...getState().fetchDefault.requestInit,
			signal: abortController ? abortController.signal : undefined,
		}),
	).then((res) => (Array.isArray(res) ? res : [res]));
}

export function serviceCategoryToKey(scKey: IDomainValue['key']): string {
	return 'c' + scKey;
}
export function keyToServiceCategory(key: string): IDomainValue['key'] | null {
	return Number(key.substring(1)) || null;
}

export function groupServices(shopBookingTemplates: IServicesList[]): IServicesList[][] {
	const servicesMap: { [variantParentId: string]: IServicesList[] } = {};
	for (const service of shopBookingTemplates) {
		if (!(service.variantParentId in servicesMap)) {
			servicesMap[service.variantParentId] = [];
		}
		servicesMap[service.variantParentId].push(service);
	}

	return Object.values(servicesMap);
}

export function initialFormValues(state: IStore, shopId: string, officeId: string): Partial<IServiceGroupsForm> {
	const shopBookingTemplates =
		state.shopBookingTemplates.services || []?.filter((sbt) => sbt.shopId === shopId && sbt.officeId === officeId);
	const officeServiceGroups = state.serviceGroups.serviceGroups?.filter((sbt) => sbt.officeId === officeId);
	const groupedServices = groupServices(shopBookingTemplates);

	if (!shopBookingTemplates || !officeServiceGroups) {
		return null;
	}

	const serviceGroupToTree = (sg: IServiceGroup) => ({
		...sg,
		children: getChildren(sg.id),
	});

	const sortByOrder = <T extends { order: number }>(a: T, b: T) => a.order - b.order;
	const getChildren = (sgid: IServiceGroup['id']): IServiceTree['children'] => [
		...groupedServices.filter((gs) => gs[0]?.serviceGroupId === sgid),
		...officeServiceGroups
			.filter((ssg) => ssg.parentId === sgid)
			.sort(sortByOrder)
			.map(serviceGroupToTree),
	];
	const byCategory: { [categoryId: string]: IServiceGroup[] } = {};
	for (const ssg of officeServiceGroups) {
		if (ssg.parentId) {
			continue;
		}

		const key = ssg.serviceCategoryId ? serviceCategoryToKey(ssg.serviceCategoryId) : UNLINKED_KEY;

		if (!(key in byCategory)) {
			byCategory[key] = [];
		}

		byCategory[key].push(ssg);
	}

	const form: Partial<IServiceGroupsForm> = {};
	const tempServiceCategoryTree: IServiceTree[][] = [];

	for (const key in byCategory) {
		tempServiceCategoryTree.push(byCategory[key].sort(sortByOrder).map(serviceGroupToTree));
	}

	// Sort by service group order
	tempServiceCategoryTree.sort((a, b) => a[0]?.order - b[0]?.order);

	form.serviceCategoryTree = tempServiceCategoryTree;
	form.numberOfTimesServiceGroupToggled = 0;

	return form;
}
